import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { frameScoreAPI } from '../services/api';

const DESTINO_COLORS = {
  reproductor: 'bg-green-100 text-green-800',
  invernada: 'bg-amber-100 text-amber-800',
  faena: 'bg-red-100 text-red-800',
  recria: 'bg-blue-100 text-blue-800',
};

const EF_COLORS = {
  excelente: 'bg-green-100 text-green-800',
  buena: 'bg-blue-100 text-blue-800',
  regular: 'bg-amber-100 text-amber-800',
  mala: 'bg-red-100 text-red-800',
};

const FrameScorePage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('frame');
  const [loading, setLoading] = useState(true);
  const [frameData, setFrameData] = useState(null);
  const [criaData, setCriaData] = useState(null);
  const [filters, setFilters] = useState({ edad_min: 0, edad_max: 120, sexo: '' });

  useEffect(() => {
    if (user?.establecimiento_id) loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [frameRes, criaRes] = await Promise.allSettled([
        frameScoreAPI.evaluacion(user.establecimiento_id, filters),
        frameScoreAPI.eficienciaCria(user.establecimiento_id),
      ]);
      if (frameRes.status === 'fulfilled') setFrameData(frameRes.value.data);
      if (criaRes.status === 'fulfilled') setCriaData(criaRes.value.data);
    } catch { /* silent */ }
    setLoading(false);
  };

  const applyFilters = () => loadData();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-gray-600">Cargando evaluacion...</p>
        </div>
      </div>
    );
  }

  const res = frameData?.resumen || {};
  const evals = frameData?.evaluaciones || [];
  const criaRes = criaData?.resumen || {};
  const vacas = criaData?.vacas || [];

  const tabs = [
    { id: 'frame', label: 'Frame Score' },
    { id: 'cria', label: 'Eficiencia de Cria' },
    { id: 'economia', label: 'Analisis Economico' },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="frame-score-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Evaluacion Frame Score & Eficiencia</h2>
        <p className="text-sm text-gray-500 mt-1">Analisis de estructura corporal, eficiencia de cria y destino productivo</p>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-100 rounded-lg p-1 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${tab === t.id ? 'bg-white text-green-700 font-semibold shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ===== TAB 1: FRAME SCORE ===== */}
      {tab === 'frame' && (
        <div className="space-y-6" data-testid="frame-tab-content">
          {/* Resumen Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <SummaryCard label="Evaluados" value={res.total_evaluados || 0} color="text-gray-900" border="border-gray-300" />
            <SummaryCard label="Con Frame" value={res.con_frame || 0} color="text-blue-700" border="border-blue-400" />
            <SummaryCard label="Frame Promedio" value={res.frame_promedio || '—'} color="text-indigo-700" border="border-indigo-400" />
            <SummaryCard label="Reproductores" value={res.reproductores || 0} color="text-green-700" border="border-green-400" />
            <SummaryCard label="Invernada" value={res.invernada || 0} color="text-amber-700" border="border-amber-400" />
            <SummaryCard label="Faena" value={res.faena || 0} color="text-red-700" border="border-red-400" />
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-4 items-end" data-testid="frame-filters">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Edad Min (meses)</label>
              <input type="number" min={0} max={120} value={filters.edad_min} onChange={e => setFilters(f => ({...f, edad_min: +e.target.value}))} className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Edad Max (meses)</label>
              <input type="number" min={0} max={240} value={filters.edad_max} onChange={e => setFilters(f => ({...f, edad_max: +e.target.value}))} className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sexo</label>
              <select value={filters.sexo} onChange={e => setFilters(f => ({...f, sexo: e.target.value}))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Todos</option>
                <option value="macho">Machos</option>
                <option value="hembra">Hembras</option>
              </select>
            </div>
            <button onClick={applyFilters} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition" data-testid="apply-filters-btn">
              Filtrar
            </button>
            <button onClick={() => { setFilters({ edad_min: 12, edad_max: 18, sexo: '' }); }} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-100 transition" data-testid="filter-14-15-btn">
              Solo 12-18 meses
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="frame-table">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['VID', 'Sexo', 'Raza', 'Edad', 'Peso (kg)', 'Altura (cm)', 'Frame', 'kg/cm', 'Destino'].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {evals.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-8 text-gray-400">Sin animales en el rango seleccionado</td></tr>
                  )}
                  {evals.map((ev, i) => (
                    <tr key={ev.animal_id} className={`border-b border-gray-100 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                      <td className="py-3 px-4 font-semibold">{ev.VID}</td>
                      <td className="py-3 px-4 capitalize">{ev.sexo === 'macho' ? 'M' : 'H'}</td>
                      <td className="py-3 px-4 text-gray-600">{ev.raza || '—'}</td>
                      <td className="py-3 px-4">{ev.edad_meses}m</td>
                      <td className="py-3 px-4">{ev.peso ? `${ev.peso}` : '—'}</td>
                      <td className="py-3 px-4">{ev.altura_cadera ? `${ev.altura_cadera}` : '—'}</td>
                      <td className="py-3 px-4">
                        {ev.frame_score != null ? (
                          <span className="font-bold text-lg">{ev.frame_score}</span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-3 px-4">{ev.ratio_kg_cm ? `${ev.ratio_kg_cm}` : '—'}</td>
                      <td className="py-3 px-4">
                        {ev.destino ? (
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold capitalize ${DESTINO_COLORS[ev.destino.destino] || 'bg-gray-100 text-gray-700'}`}>
                            {ev.destino.destino}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Destino Distribution */}
          {res.con_frame > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="destino-distribution">
              <h3 className="text-base font-bold text-gray-900 mb-4">Distribucion por Destino</h3>
              <div className="flex gap-2 h-8 rounded-full overflow-hidden">
                {[
                  { key: 'reproductores', color: 'bg-green-500', label: 'Reprod.' },
                  { key: 'invernada', color: 'bg-amber-500', label: 'Invernada' },
                  { key: 'faena', color: 'bg-red-500', label: 'Faena' },
                  { key: 'recria', color: 'bg-blue-500', label: 'Recria' },
                ].filter(d => (res[d.key] || 0) > 0).map(d => {
                  const pct = Math.round((res[d.key] / res.con_frame) * 100);
                  return (
                    <div key={d.key} className={`${d.color} flex items-center justify-center text-white text-xs font-bold transition-all`} style={{ width: `${pct}%`, minWidth: pct > 0 ? '40px' : 0 }} title={`${d.label}: ${res[d.key]} (${pct}%)`}>
                      {pct > 10 ? `${d.label} ${pct}%` : `${pct}%`}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== TAB 2: EFICIENCIA DE CRIA ===== */}
      {tab === 'cria' && (
        <div className="space-y-6" data-testid="cria-tab-content">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
            <h3 className="text-base font-bold text-green-900 mb-2">Eficiencia de Cria = Peso Ternero al Destete / Peso Madre x 100</h3>
            <p className="text-sm text-green-700">
              Una vaca de 400 kg que desteta un ternero de 200 kg = <strong>50%</strong> (excelente).
              Una vaca de 700 kg que desteta un ternero de 200 kg = <strong>28.5%</strong> (mala → descarte).
            </p>
          </div>

          {/* Resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <SummaryCard label="Vacas Evaluadas" value={criaRes.total || 0} color="text-gray-900" border="border-gray-300" />
            <SummaryCard label="EP Promedio" value={criaRes.eficiencia_promedio ? `${criaRes.eficiencia_promedio}%` : '—'} color="text-blue-700" border="border-blue-400" />
            <SummaryCard label="Excelentes" value={criaRes.excelentes || 0} color="text-green-700" border="border-green-400" />
            <SummaryCard label="Buenas" value={criaRes.buenas || 0} color="text-blue-700" border="border-blue-400" />
            <SummaryCard label="Regulares" value={criaRes.regulares || 0} color="text-amber-700" border="border-amber-400" />
            <SummaryCard label="Descarte" value={criaRes.malas || 0} color="text-red-700" border="border-red-400" />
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="cria-table">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['#', 'VID', 'Raza', 'Peso Madre', 'Peso Destete', 'Terneros', 'Kg Acum.', 'Eficiencia', 'Estado'].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vacas.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-8 text-gray-400">Sin datos de pariciones con peso al destete</td></tr>
                  )}
                  {vacas.map((v, i) => (
                    <tr key={v.animal_id} className={`border-b border-gray-100 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                      <td className="py-3 px-4 font-semibold text-gray-400">{v.ranking}</td>
                      <td className="py-3 px-4 font-semibold">{v.VID}</td>
                      <td className="py-3 px-4 text-gray-600">{v.raza || '—'}</td>
                      <td className="py-3 px-4">{v.peso_madre} kg</td>
                      <td className="py-3 px-4">{v.peso_destete_promedio} kg</td>
                      <td className="py-3 px-4">{v.total_terneros}</td>
                      <td className="py-3 px-4 font-semibold">{v.kg_acumulados} kg</td>
                      <td className="py-3 px-4">
                        <span className="text-lg font-bold">{v.eficiencia}%</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold capitalize ${EF_COLORS[v.clasificacion] || 'bg-gray-100'}`}>
                          {v.clasificacion}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== TAB 3: ANALISIS ECONOMICO ===== */}
      {tab === 'economia' && (
        <div className="space-y-6" data-testid="economia-tab-content">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
            <h3 className="text-base font-bold text-amber-900 mb-2">Analisis Economico por Frame Score</h3>
            <p className="text-sm text-amber-700">
              El Frame Score determina el peso de faena proyectado. Animales con frame 5-7 son optimos para feedlot: mejor ganancia diaria y valor de mercado.
            </p>
          </div>

          {/* Peso de Faena por Frame */}
          <div className="bg-white rounded-xl shadow-sm p-6" data-testid="peso-faena-table">
            <h3 className="text-base font-bold text-gray-900 mb-4">Peso de Faena Proyectado por Frame Score</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['Frame', 'Novillo (kg)', 'Vaquillona (kg)', 'Vaca Madura (kg)', 'Toro (kg)'].map(h => (
                      <th key={h} className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[1,2,3,4,5,6,7,8,9].map(f => (
                    <tr key={f} className={`border-b border-gray-100 ${f >= 5 && f <= 7 ? 'bg-green-50' : ''}`}>
                      <td className="py-2 px-4 text-center font-bold text-lg">{f}</td>
                      <td className="py-2 px-4 text-center">{[340,385,430,475,520,570,615,660,705][f-1]}</td>
                      <td className="py-2 px-4 text-center">{[295,340,385,430,475,520,570,615,660][f-1]}</td>
                      <td className="py-2 px-4 text-center">{[340,385,430,500,545,590,635,680,725][f-1]}</td>
                      <td className="py-2 px-4 text-center">{[545,635,725,815,910,1000,1090,1180,1270][f-1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-400 mt-2">* Frame 5-7 (resaltado) = rango optimo para feedlot</p>
            </div>
          </div>

          {/* Costo/Beneficio por Sistema */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { sistema: 'Pastoril', gdp: '0.6 kg/dia', costo: 'USD 0.80/dia', desc: 'Menor costo, mas dias de engorde' },
              { sistema: 'Semi-Intensivo', gdp: '1.0 kg/dia', costo: 'USD 1.50/dia', desc: 'Balance costo-velocidad optimo' },
              { sistema: 'Feedlot Intensivo', gdp: '1.5 kg/dia', costo: 'USD 2.50/dia', desc: 'Rapido engorde, mayor inversion' },
            ].map(s => (
              <div key={s.sistema} className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-amber-400" data-testid={`sistema-${s.sistema.toLowerCase().replace(/\s+/g, '-')}`}>
                <h4 className="font-bold text-gray-900">{s.sistema}</h4>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">GDP estimada:</span><span className="font-semibold">{s.gdp}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Costo diario:</span><span className="font-semibold">{s.costo}</span></div>
                </div>
                <p className="mt-3 text-xs text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, color, border }) => (
  <div className={`rounded-xl bg-white shadow-sm p-4 border-l-4 ${border}`}>
    <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
  </div>
);

export default FrameScorePage;
